import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowForward, EmojiEvents, Payments, VolunteerActivism, WorkspacePremium } from '@mui/icons-material';
import { baseURL } from '../../Constants';
import { getAllDonators } from '../../services/DonatorService';
import { Donator } from '../../models/Donator';
import Loading from '../../components/Loading';
import styles from './DonationsPage2.module.css';

// 480 is plenty: the endpoint caps at 800 and the widest figure renders at 240 CSS px.
const FIGURE_SOURCE_WIDTH = 480;

// /Minigame/real-photo serves TWO different kinds of image, and the podium has to tell them apart:
//   - league photos (the ~93% case) are transparent PNG cut-outs — subject on an empty frame, head
//     to waist. These stand free on the pedestal, no container, which is the whole point of the
//     podium: a ring or a circle around a cut-out just draws an empty disc with a body inside it.
//   - custom uploads are arbitrary opaque rectangles. A rectangle can't stand free, so it gets a
//     framed plaque instead.
// The endpoint sends `access-control-allow-origin: *`, so we can settle it exactly: draw the loaded
// image small and measure how much of its own alpha bounding box the subject fills. Corner samples
// are NOT enough — an upload is usually a rectangle with a transparent margin, so its corners are
// transparent too. Measured over 24 league photos the silhouettes fill 0.59–0.71 of their box; an
// opaque rectangle fills 1.00.
const CUTOUT_MAX_FILL = 0.85;

const isCutout = (img: HTMLImageElement): boolean => {
    const size = 32;
    try {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return true;
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        let minX = size, maxX = -1, minY = size, maxY = -1, opaque = 0;
        for (let y = 0; y < size; y++) {
            for (let x = 0; x < size; x++) {
                if (data[(y * size + x) * 4 + 3] <= 128) continue;
                opaque++;
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }
        if (maxX < 0) return true; // fully transparent — nothing to frame
        return opaque / ((maxX - minX + 1) * (maxY - minY + 1)) < CUTOUT_MAX_FILL;
    } catch {
        return true; // tainted canvas (CORS gone) — assume the common case rather than break
    }
};

// Podium figure: the donor's REAL photo — our custom upload first, else a non-placeholder dpl.gg
// photo — via /Minigame/real-photo, which 404s when only the black silhouette exists. The image is
// shown only once it actually loads; until then (and forever, if it 404s) the rank shield stays. So
// we never show the dpl.gg placeholder, and we don't rely on the stale hasPlayerImage/badges list.
const PodiumFigure = ({ steamId, rank, color, first }: {
    steamId: string | null;
    rank: number;
    color: string;
    first: boolean;
}) => {
    const [kind, setKind] = useState<'pending' | 'cutout' | 'photo'>('pending');
    // If the CORS request ever fails we reload the plain image and skip the probe, so a header
    // regression costs us the framing choice, not the photo.
    const [corsFailed, setCorsFailed] = useState(false);
    const shield = (
        <span
            className={`${styles.rankShield} ${first ? styles.rankShieldFirst : ''}`}
            style={{ color, borderColor: `${color}3d`, background: `linear-gradient(180deg, ${color}1f 0%, ${color}08 100%)` }}
        >
            {rank}
        </span>
    );
    if (!steamId) return shield;
    const loaded = kind !== 'pending';
    return (
        <>
            {!loaded && shield}
            {/* Watermark numeral behind the figure — only once there IS a figure to sit behind. */}
            <span
                className={`${styles.ghostRank} ${first ? styles.ghostRankFirst : ''}`}
                style={{ color: `${color}14`, display: loaded ? 'block' : 'none' }}
                aria-hidden="true"
            >
                {rank}
            </span>
            <img
                key={corsFailed ? 'plain' : 'cors'}
                crossOrigin={corsFailed ? undefined : 'anonymous'}
                src={`${baseURL}/Minigame/real-photo/${steamId}?width=${FIGURE_SOURCE_WIDTH}`}
                onLoad={(e) => setKind(corsFailed || isCutout(e.currentTarget) ? 'cutout' : 'photo')}
                onError={() => setCorsFailed(true)}
                alt=""
                className={`${styles.figure} ${first ? styles.figureFirst : ''} ${kind === 'photo' ? styles.figurePhoto : ''}`}
                style={{ display: loaded ? 'block' : 'none', borderColor: kind === 'photo' ? `${color}40` : undefined }}
            />
        </>
    );
};

const MOBILEPAY_URL = 'https://qr.mobilepay.dk/box/9229be2b-bcf7-4f26-acfc-e43ed97e7a5d/pay-in';

interface AggregatedDonor {
    steamId: string | null;
    name: string;
    alias: string;
    full: string;
    donationAmount: number;
}

// 'Mathias "Blobber" Blaabjerg' → alias "Blobber", full "Mathias Blaabjerg"
const parseName = (name: string): { alias: string; full: string } => {
    const m = name.match(/^(.*?)\s*"([^"]+)"\s*(.*)$/);
    if (m) return { alias: m[2], full: `${m[1]} ${m[3]}`.trim() };
    return { alias: name, full: '' };
};

const fmt = (n: number) => n.toLocaleString('da-DK');

// The Hall of Fame, served at /donations. Replaced the original at the season 33 launch;
// /donations2 redirects here.
const DonationsPage2 = () => {
    const { t } = useTranslation('pages');
    const [donators, setDonators] = useState<Donator[]>([]);
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        getAllDonators()
            .then(setDonators)
            .catch(() => setDonators([]))
            .finally(() => setLoading(false));
    }, []);

    // Aggregate donations per person (same person can donate multiple times), sorted by total
    const sorted = useMemo<AggregatedDonor[]>(() => {
        const aggregated = donators.reduce((acc, donor) => {
            const existing = acc.find(d =>
                (d.steamId && d.steamId === donor.steamId) || (!d.steamId && d.name === donor.name));
            if (existing) {
                existing.donationAmount += donor.donationAmount;
            } else {
                acc.push({
                    steamId: donor.steamId,
                    name: donor.name,
                    ...parseName(donor.name),
                    donationAmount: donor.donationAmount,
                });
            }
            return acc;
        }, [] as AggregatedDonor[]);
        return aggregated.sort((a, b) => b.donationAmount - a.donationAmount);
    }, [donators]);

    const podium = sorted.slice(0, 3);
    const kingsRest = sorted.filter(d => d.donationAmount >= 200).slice(3);
    const supporters = sorted.filter(d => d.donationAmount < 200);
    const supportersStartRank = sorted.filter(d => d.donationAmount >= 200).length + 1;

    const renderName = (donor: AggregatedDonor, className: string) =>
        donor.steamId ? (
            <Link to={`/player/${donor.steamId}`} className={className}>{donor.alias}</Link>
        ) : (
            <span className={className}>{donor.alias}</span>
        );

    const renderRow = (donor: AggregatedDonor, index: number, rank: number, amountClass: string) => (
        <div
            className={`${styles.donorRow} ${index % 2 === 0 ? styles.rowEven : styles.rowOdd}`}
            key={donor.steamId ?? donor.name}
        >
            <span className={styles.donorRank}>{rank}</span>
            <div className={styles.donorName}>
                {renderName(donor, styles.donorAlias)}
                {donor.full && <span className={styles.donorFull}>{donor.full}</span>}
            </div>
            <span className={`${styles.donorAmount} ${amountClass}`}>{fmt(donor.donationAmount)} kr.</span>
        </div>
    );


    if (loading) {
        return <div className={styles.page}><div className="text-center" style={{ paddingTop: 60 }}><Loading /></div></div>;
    }

    // Podium in visual order 2nd / 1st / 3rd — kept at three columns on every width, just scaled down.
    const podiumOrder = [
        { donor: podium[1], place: t('hallOfFame.place2'), variant: 'second' as const, rank: 2 },
        { donor: podium[0], place: t('hallOfFame.place1'), variant: 'first' as const, rank: 1 },
        { donor: podium[2], place: t('hallOfFame.place3'), variant: 'third' as const, rank: 3 },
    ].filter((p) => p.donor);

    return (
        <div className={styles.page}>
            {/* ===== Header ===== */}
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.title}>{t('hallOfFame.title')}</h1>
                    <p className={styles.subtitle}>{t('hallOfFame.subtitle2')}</p>
                </div>
                <div className={styles.countTile}>
                    <div className={styles.countValue}>{sorted.length}</div>
                    <div className={styles.countLabel}>{t('hallOfFame.supporters')}</div>
                </div>
            </div>

            {sorted.length === 0 ? (
                <p className={styles.emptyState}>{t('hallOfFame.noDonors')}</p>
            ) : (
                <>
                    {/* ===== Podium ===== */}
                    <div className={styles.podium}>
                        {podiumOrder.map(({ donor, place, variant, rank }) => {
                            const first = variant === 'first';
                            const pedestalClass = first ? styles.pedestalFirst : variant === 'second' ? styles.pedestalSecond : styles.pedestalThird;
                            const placeColor = first ? '#f1c40f' : variant === 'second' ? '#d0d0d0' : '#c8895c';
                            return (
                                <div className={`${styles.podiumCol} ${first ? styles.podiumColFirst : ''}`} key={donor.steamId ?? donor.name}>
                                    {first && <span className={styles.kingGlow} aria-hidden="true" />}
                                    <div className={`${styles.figureZone} ${first ? styles.figureZoneFirst : ''}`}>
                                        <PodiumFigure steamId={donor.steamId} rank={rank} color={placeColor} first={first} />
                                    </div>
                                    {/* The pedestal paints over the figure's bottom edge, hiding the waist crop. */}
                                    <div className={`${styles.pedestal} ${pedestalClass}`}>
                                        <div className={styles.podiumPlace} style={{ color: placeColor }}>
                                            {first && <EmojiEvents sx={{ fontSize: 15 }} />}
                                            {place}
                                        </div>
                                        <div>
                                            {renderName(donor, `${styles.podiumAlias} ${first ? styles.podiumAliasFirst : ''}`)}
                                        </div>
                                        {donor.full && <div className={styles.podiumFull}>{donor.full}</div>}
                                        <div className={`${styles.podiumRule} ${first ? styles.podiumRuleFirst : ''}`} />
                                        <div className={`${styles.podiumAmount} ${first ? styles.podiumAmountFirst : ''}`}>
                                            {fmt(donor.donationAmount)} kr.
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* ===== Kongerne ===== */}
                    {kingsRest.length > 0 && (
                        <>
                            <div className={styles.bandHeader}>
                                <WorkspacePremium className={styles.bandIconKings} sx={{ fontSize: 22 }} />
                                <h2 className={styles.bandTitle}>{t('hallOfFame.kings')}</h2>
                                <span className={`${styles.bandTag} ${styles.bandTagKings}`}>{t('hallOfFame.rangeKings')}</span>
                                <Link to="/premium" className={styles.bandLink}>
                                    {t('hallOfFame.seeWhatYouGet')}
                                    <ArrowForward sx={{ fontSize: 16 }} />
                                </Link>
                            </div>
                            <p className={styles.bandNote}>{t('hallOfFame.kingsNote')}</p>
                            <div className={styles.bandList}>
                                {kingsRest.map((donor, i) => renderRow(donor, i, i + 4, styles.amountKings))}
                            </div>
                        </>
                    )}

                    {/* ===== Alle støtter ===== */}
                    {supporters.length > 0 && (
                        <>
                            <div className={styles.bandHeader}>
                                <VolunteerActivism className={styles.bandIconSupporters} sx={{ fontSize: 22 }} />
                                <h2 className={styles.bandTitle}>{t('hallOfFame.allSupporters')}</h2>
                                <span className={`${styles.bandTag} ${styles.bandTagSupporters}`}>{t('hallOfFame.rangeSupporters')}</span>
                                <Link to="/premium" className={styles.bandLink}>
                                    {t('hallOfFame.seeWhatYouGet')}
                                    <ArrowForward sx={{ fontSize: 16 }} />
                                </Link>
                            </div>
                            <p className={styles.bandNote}>{t('hallOfFame.supportersNote')}</p>
                            <div className={`${styles.bandList} ${styles.bandListTwoCol}`}>
                                {supporters.map((donor, i) => renderRow(donor, i, supportersStartRank + i, styles.amountSupporters))}
                            </div>
                        </>
                    )}
                </>
            )}

            {/* ===== CTA ===== */}
            <div className={styles.cta}>
                <div className={styles.ctaLeft}>
                    <span className={styles.ctaIcon}>
                        <VolunteerActivism sx={{ fontSize: 26 }} />
                    </span>
                    <div>
                        <div className={styles.ctaTitle}>{t('hallOfFame.ctaTitle')}</div>
                        <div className={styles.ctaDesc}>{t('hallOfFame.ctaDesc')}</div>
                    </div>
                </div>
                <a href={MOBILEPAY_URL} target="_blank" rel="noreferrer" className={styles.ctaButton}>
                    <Payments sx={{ fontSize: 20 }} />
                    {t('hallOfFame.supportButton')}
                </a>
            </div>

            <p className={styles.anonymousNote}>
                {t('hallOfFame.anonymousNote')}{' '}
                <a href="https://discord.gg/W54Zhj2PD3" target="_blank" rel="noreferrer">Discord</a>{' '}
                {t('hallOfFame.afterDonation')}
            </p>
        </div>
    );
};

export default DonationsPage2;
